export const isEmptyObject = <T extends object>(obj: T): boolean => !Object.keys(obj).length;
export type NullableOptional<T> = {
  [K in keyof T]?: T[K] | null | undefined;
};

export type MakeRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/**
 * @function unflattenFromDotNotation
 * @example { a: 5, "b.c": 6, "b.d": 7 } -> { a: 5, b: { c: 6, d: 7 }}
 * @param obj The object to act on
 * @returns The object but with dot-notation keys expanded back into nested objects
 */
export function unflattenFromDotNotation(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      key.split(".").reduce((nested: Record<string, unknown>, k, i, keys) => {
        if (i === keys.length - 1) nested[k] = value;
        else if (typeof nested[k] !== "object" || nested[k] === null) nested[k] = {};
        return nested[k] as Record<string, unknown>;
      }, acc);
      return acc;
    },
    {} as Record<string, unknown>
  );
}

/**
 * @function flattenToDotNotation
 * @example { a: 5, b: { c: 6, d: 7 }} -> { a: 5, "b.c": 6, "b.d": 7 }
 * @param obj The object to act on
 * @param prefix A prefix to add to keys
 * @returns The object but with each nested set of key(s) mapped to a single string
 */
export function flattenToDotNotation(obj: Record<string, unknown>, prefix = ""): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (value !== null && value !== undefined && typeof value === "object" && !Array.isArray(value))
        Object.assign(acc, flattenToDotNotation(value as Record<string, unknown>, fullKey));
      else if (value !== undefined) acc[fullKey] = value;

      return acc;
    },
    {} as Record<string, unknown>
  );
}
